import { Grid, Button, FormControl, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { checkRiskCheckAdditional } from 'src/store/thunks/riskCheck.thunk';
import {
  addQuestionaire,
  removeQuestionaireStep,
  reverseVisibility,
  setLabelQuestionaire,
  setOpenNoticeTrue,
  setRiskIndex,
} from 'src/store/reducers/riskCheck.reducer';
import { getComputedValues, getInputs } from 'src/utils';
import { useAppDispatch, useAppSelector } from 'src/utils/hooks';
import ListQuestions from './ListQuestions';
import { useRef, useMemo } from 'react';
import { decrementStep } from 'src/store/reducers/stepper.reducers';
import { updateAnsweredCustomQuestions } from 'src/store/reducers/config.reducer';
import mandants from 'src/config/mandant';

interface Props {
  questionaire: Questionnaire;
  submitText: string;
  personId: string;
  riskIdx: number;
  personIdx: number;
  readonly: boolean;
}

export default function DynamicForm({
  questionaire,
  submitText,
  personId,
  personIdx,
  riskIdx,
  readonly,
}: Props) {
  const { mandant } = useAppSelector((state) => state.config);
  type Mandant = keyof typeof mandants;
  const stepBackAllowed: boolean =
    mandants[mandant as Mandant]?.stepBackAllowed;
  const { isColorBlindMode } = useAppSelector((state) => state.config);
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const { questionnaireStep, questionnaireStepTotal } = useAppSelector(
    (state) =>
      state.riskCheck.selectedInsurences[personIdx - 1].riskCheckOptions[
        riskIdx
      ]
  );
  const { riskCheckIdx } = useAppSelector(
    (state) =>
      state.riskCheck.selectedInsurences[personIdx - 1]
  );

  const { steps } = useAppSelector((state) => state.stepper);

  const { initialValues, questions, validationSchema } = useMemo(() => {
    return getInputs(
      questionaire,
      questionnaireStep,
      questionnaireStepTotal,
      readonly
    );
  }, [questionaire, questionnaireStep, questionnaireStepTotal, readonly]);

  return (
    <Formik
      key={questionnaireStep}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={false}
      validateOnMount={true}
      validateOnBlur={true}
      validateOnChange={true}
      onSubmit={(values) => {
        const trimmedValues: { [key: string]: any } = {};

        for (const key in values) {
          if (values.hasOwnProperty(key)) {
            let newKey: string = key.replace(/\$/g, '[]').replace(/&/g, '.');
            const value = values[key];
            if (newKey === 'orderIban') {
              trimmedValues[newKey] =
                typeof value === 'string' ? value.replaceAll(/\s/g, '') : value;
            } else {
              trimmedValues[newKey] =
                typeof value === 'string' ? value.trim() : value;
            }
          }
        }

        const trimmedValuesVisible: { [key: string]: any } = {};
        // filter only visible
        const setOnlyVisibleValuesRecursive = (questions: Question[]) => {
          if (!questions || questions.length === 0) {
            return;
          }
          questions.forEach((question) => {
            if (trimmedValues.hasOwnProperty(question.key)) {
              trimmedValuesVisible[question.key] = question.visible
                ? trimmedValues[question.key]
                : null;
            }
            if (question?.childQuestionary) {
              question?.childQuestionary.forEach((child: any) => {
                setOnlyVisibleValuesRecursive(child.questions);
              });
            }
          });
        };

        if (trimmedValues) {
          setOnlyVisibleValuesRecursive(questions);
        }

        const computed = getComputedValues(questions, trimmedValuesVisible);

        if (!readonly) {
          dispatch(
            setLabelQuestionaire({
              personIdx: personIdx - 1,
              riskIdx: riskIdx,
              questionnaireStep,
            })
          );

          dispatch(
            addQuestionaire({
              personIdx: personIdx - 1,
              values: { ...trimmedValuesVisible, ...computed },
            })
          );

          dispatch(
            checkRiskCheckAdditional({
              Id: personId,
              personIdx: personIdx - 1,
              riskIdx,
              values: { ...trimmedValuesVisible, ...computed },
            } as RiskCheckThunkProps)
          );
        }
      }}
    >
      {({ isValid, validateForm }) => {
        // console.log("questions ...", questions);
        return (
          <Form ref={formRef}>
            <ListQuestions
              questions={questions}
              personId={personId}
              riskIdx={riskIdx}
              personIdx={personIdx}
              formRef={formRef}
              readonly={readonly}
              validateForm={validateForm}
            />
            <Grid
              display={'flex'}
              flexDirection={'row-reverse'}
              justifyContent={'space-between'}
              pb={2}
            >
              {questions.length > 0 ? (
                <FormControl sx={{ width: '200px' }}>
                  {!readonly && (
                    <Button
                      variant="contained"
                      size="large"
                      type="submit"
                      onClick={() => {
                        validateForm();
                      }}
                      disabled={!isValid}
                      sx={
                        isColorBlindMode
                          ? {
                              backgroundColor: '#000',
                              textDecoration: 'underline',
                              textUnderlineOffset: '2.5px',
                              color: '#fff',
                              '&:hover': {
                                backgroundColor: '#222',
                                textDecoration: 'underline',
                                color: '#fff',
                              },
                              '&.Mui-disabled': {
                                backgroundColor: '#747474',
                                color: '#fff',
                              },
                            }
                          : {}
                      }
                    >
                      {submitText}
                    </Button>
                  )}
                </FormControl>
              ) : (
                <Typography variant="h6">Alle Fragen beantwortet</Typography>
              )}
              <FormControl>
                {stepBackAllowed && questionnaireStep > 0 && (
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (steps === 1 && questionnaireStep === 1) {
                        if (riskIdx === 0 || (riskCheckIdx >= 0 && riskCheckIdx < 1e3 ) ) {
                          dispatch(
                            setOpenNoticeTrue({ personIdx: personIdx - 1 })
                          );
                          dispatch(decrementStep());
                        } else {
                          dispatch(
                            setRiskIndex({
                              personIdx: personIdx - 1,
                              riskIdx: riskIdx * 1e3 + riskIdx,
                            })
                          );
                          dispatch(
                            removeQuestionaireStep({
                              personIdx: personIdx - 1,
                              riskIdx: riskIdx - 1,
                            })
                          );
                          dispatch(
                            reverseVisibility({
                              personIdx: personIdx - 1,
                              riskIdx: riskIdx - 1,
                            })
                          );
                        }
                      } else if (steps === 2) {
                        dispatch(
                          reverseVisibility({
                            personIdx: personIdx - 1,
                            riskIdx,
                          })
                        );
                        dispatch(
                          removeQuestionaireStep({
                            personIdx: personIdx - 1,
                            riskIdx,
                          })
                        );
                        dispatch(decrementStep());
                      } else {
                        dispatch(updateAnsweredCustomQuestions(false));
                        dispatch(
                          removeQuestionaireStep({
                            personIdx: personIdx - 1,
                            riskIdx,
                          })
                        );
                      }
                    }}
                    sx={
                      isColorBlindMode
                        ? {
                            backgroundColor: 'accessibility.button.main',
                            color: 'accessibility.button.contrastText',
                            textDecoration: 'underline',
                            textUnderlineOffset: '2.5px',
                            '&:hover': {
                              backgroundColor: '#222',
                              color: 'accessibility.button.contrastText',
                              textDecoration: 'underline',
                            },
                          }
                        : {}
                    }
                  >
                    Zurück
                  </Button>
                )}
              </FormControl>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
